import { useMemo, useState } from "react";

import { useStock } from "@/api/catalog";
import { useMenus } from "@/api/menus";
import { FreshTile } from "@/components/FreshTile";
import { useT } from "@/lib/i18n";

interface CategoryHit {
  category_id: number;
  category_name: string;
  image_url: string | null;
  services_count: number;
  matched_service?: string;
}

/**
 * Fresh catalog landing.
 * - Empty search → menu grid (browse mode).
 * - Non-empty search → flat list of categories whose name or any
 *   nested service name contains the query. Lets the client jump
 *   straight from "borderlands" / "100 try" / "valorant" to the buy
 *   page without picking the right menu first.
 */
export function ExploreFresh() {
  const menus = useMenus();
  const stock = useStock();
  const t = useT();
  const [q, setQ] = useState("");

  const trimmed = q.trim().toLowerCase();
  const isSearching = trimmed.length > 0;

  const filteredMenus = useMemo(() => {
    return menus.data?.menus ?? [];
  }, [menus.data]);

  const categoryHits: CategoryHit[] = useMemo(() => {
    if (!isSearching || !stock.data) return [];
    const out: CategoryHit[] = [];
    for (const c of stock.data.categories) {
      const nameMatch = c.category_name.toLowerCase().includes(trimmed);
      const matchedService = !nameMatch
        ? c.services.find((s) =>
            s.service_name.toLowerCase().includes(trimmed),
          )
        : undefined;
      if (!nameMatch && !matchedService) continue;
      out.push({
        category_id: c.category_id,
        category_name: c.category_name,
        image_url: c.image_url,
        services_count: c.services.length,
        matched_service: matchedService?.service_name,
      });
    }
    return out;
  }, [isSearching, stock.data, trimmed]);

  return (
    <div>
      <h1 style={{
        fontSize: 26,
        fontWeight: 700,
        color: "var(--color-text)",
        letterSpacing: "-0.01em",
      }}>
        {t("explore.title")}
      </h1>
      <p style={{ color: "var(--color-text-muted)", marginTop: 4, marginBottom: 20 }}>
        {isSearching ? t("explore.subtitle.search") : t("explore.subtitle.browse")}
      </p>

      <div className="fresh-search-bar">
        <svg
          xmlns="http://www.w3.org/2000/svg" width="18" height="18"
          viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ color: "var(--color-text-muted)" }}
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="search"
          placeholder={t("explore.search.placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          {isSearching
            ? `${categoryHits.length.toLocaleString()} ${
                categoryHits.length === 1
                  ? t("explore.categories.one")
                  : t("explore.categories.many")
              }`
            : `${filteredMenus.length.toLocaleString()} ${
                filteredMenus.length === 1
                  ? t("explore.menus.one")
                  : t("explore.menus.many")
              }`}
        </span>
      </div>

      {(isSearching ? stock.isLoading : menus.isLoading) && (
        <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
          {t("common.loading")}
        </div>
      )}
      {(isSearching ? stock.isError : menus.isError) && (
        <div style={{
          padding: 12, borderRadius: 8,
          background: "#fee2e2", color: "#b91c1c", fontSize: 13,
        }}>
          {t("explore.failed")}
        </div>
      )}

      {/* Browse mode: menu grid */}
      {!isSearching && menus.data && filteredMenus.length > 0 && (
        <div className="fresh-tile-grid">
          {filteredMenus.map((m) => (
            <FreshTile
              key={m.id}
              to={`/menus/${m.id}`}
              title={m.name}
              subtitle={`${m.categories_count} ${
                m.categories_count === 1
                  ? t("explore.categories.one")
                  : t("explore.categories.many")
              }`}
              imageUrl={m.image_url}
            />
          ))}
        </div>
      )}

      {/* Search mode: category hits */}
      {isSearching && stock.data && categoryHits.length > 0 && (
        <div className="fresh-tile-grid">
          {categoryHits.map((c) => (
            <FreshTile
              key={c.category_id}
              to={`/categories/${c.category_id}`}
              title={c.category_name}
              subtitle={
                c.matched_service
                  ? `${t("explore.match")}: ${c.matched_service}`
                  : `${c.services_count} ${
                      c.services_count === 1
                        ? t("explore.services.one")
                        : t("explore.services.many")
                    }`
              }
              imageUrl={c.image_url}
            />
          ))}
        </div>
      )}

      {isSearching && stock.data && categoryHits.length === 0 && (
        <div style={{
          padding: 32, textAlign: "center",
          fontSize: 13, color: "var(--color-text-muted)",
        }}>
          {t("explore.empty")}
        </div>
      )}
    </div>
  );
}
